import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, preventAccidentalDismiss } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFTextArea from "@/components/form/RHFTextArea";
import { FEEDBACK_LIMITS } from "@/config/dropdownData";
import { GENERIC_ERROR } from "@/lib/errorMessages";
import { FeedbackService } from "@/services/feedbackService";

// Every ending needs a response, the good ones too: the server refuses a
// missing or blank one, and one longer than
// FEEDBACK_LIMITS.statusReasonMaxLength. The trimmed text is what is sent. A
// move between working statuses asks for none.
const END_SCHEMA = z.object({
  reason: z
    .string()
    .trim()
    .min(1, "Write a response for the person who sent it")
    .refine((v) => !v || FEEDBACK_LIMITS.hasLetter.test(v), FEEDBACK_LIMITS.messages.reasonNeedsLetter)
    .refine((v) => v.length <= FEEDBACK_LIMITS.statusReasonMaxLength, `Keep it to ${FEEDBACK_LIMITS.statusReasonMaxLength} characters`),
});
const WORKING_SCHEMA = z.object({});

/**
 * Confirms one status change. Ending the feedback also asks for a response —
 * it goes into its history, into the mail the submitter receives, and onto
 * their My Feedback. A working
 * move is a plain confirm. Either way it cannot be undone: a feedback only
 * moves forward.
 *
 * Which of the two this is comes from the row's served `end_statuses`.
 */
const StatusChangeModal = ({ feedback, toStatus, fromLabel, toLabel, onClose, onSaved }) => {
  const endStatuses = feedback.end_statuses ?? [];
  const ends = endStatuses.includes(toStatus);
  const methods = useForm({
    resolver: zodResolver(ends ? END_SCHEMA : WORKING_SCHEMA),
    // onChange: a response the server would refuse says so as it is typed.
    mode: "onChange",
    defaultValues: { reason: "" },
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async ({ reason }) => {
    try {
      const res = await FeedbackService.updateStatus(feedback.id, toStatus, ends ? reason : undefined);
      toast.success(ends ? `Marked ${toLabel} — the submitter was emailed.` : `Marked ${toLabel}.`);
      onSaved?.(res?.data ?? { ...feedback, status: toStatus });
    } catch (err) {
      toast.error(err?.userMessage || GENERIC_ERROR);
    }
  };

  const close = () => !isSubmitting && onClose?.();

  return (
    <Dialog open onOpenChange={preventAccidentalDismiss(close)}>
      <DialogContent className="w-[340px] md:w-[520px] flex flex-col">
        <DialogHeader className="relative pl-5 pr-14 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">{`Mark as ${toLabel}?`}</DialogTitle>
          <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
            From {fromLabel} to {toLabel}.{" "}
            {ends
              ? "This ends the feedback for good: the person who sent it will be emailed, with your response."
              : "The person who sent it is not emailed. It can't go back to Open afterwards."}
          </DialogDescription>
          <DialogClose
            aria-label="Close"
            disabled={isSubmitting}
            className="absolute right-4 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center"
          >
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {ends && (
              <div className="px-5 py-4">
                <RHFTextArea
                  id="feedback-status-reason"
                  name="reason"
                  label="Response"
                  placeholder="What should the person who sent it hear from us?"
                  rows={4}
                  maxLength={FEEDBACK_LIMITS.statusReasonMaxLength}
                  counter="near-limit"
                  required
                  disabled={isSubmitting}
                />
              </div>
            )}
            <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
              <Button variant="tertiary" type="button" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="secondary" disabled={isSubmitting}>
                {isSubmitting ? "Saving…" : ends ? "Confirm and email" : "Confirm"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default StatusChangeModal;
